import { map, some, sortBy } from 'lodash'
import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { IJobCost } from '../models/jobCostModel';
import { AsyncButton, Confirm, ContextMenu, CurrencyInput, Expander, Popup, sortableComponent } from './sharedComponents';
import enums from '../enum';
import { DayPickerInput } from './dayPickerInput';
import { getCollection, getModel, ModelCollection } from '../store';
import { IJobEditChildProps } from './jobEdit';
import  { ISiteVisit, SiteVisitStatus, SiteVisitType } from '../models/siteVisitModel';
import * as actions from '../actions';
import * as jobActions from '../jobActions';
import * as jobCostActions from '../jobCostActions';
import * as contractorActions from '../contractorActions';
import * as reportActions from '../reportActions';
import { useSaveRoot } from 'Scripts/saveRoot';
import { IPatchOperation, PatchOpType } from 'Scripts/modelFactory';
import * as siteVisitActions from '../siteVisitActions';
import valueFilters from 'Scripts/valueFilters';
import { IContractSelection, IOperationsModelFacade, operationsModelFacadeFactory } from '../models/operationsModelFacade';
import componentServices from 'Scripts/componentServices';
import { IContractor } from 'Scripts/models/contractorModel';
import JobNotes from './jobNotes';
import { IJob } from 'Scripts/models/jobModel';
import { IModelSet } from 'Scripts/modelSet';
import { IJobNote } from 'Scripts/models/jobNoteModel';
import jobCostLineItemFactory, { IJobCostLineItem } from 'Scripts/models/jobCostLineItemModel';
import util from 'Scripts/util';
import HistorySection from './jobCostHistory';
import InstallSheetEdit from './installSheetEdit';
import { IInstallSheet } from 'Scripts/models/installSheetModel';
import JobCostSummary from 'Scripts/models/jobCostSummary';
import { IOperationsItem } from 'Scripts/services/operationsHelper';
import globalRelay from '../globalRelay';
import { IChangeOrder } from 'Scripts/models/changeOrderModel';
import contractProxy from '../models/contractProxy';
import { getInstallSheets } from 'Scripts/schemaHelper';


const globalConfig = globalRelay.config!;

const OPS_MODAL_KEY = 'ops-modal';

function formatEmployeeDisplayName (e) {
	if(!e) {
		return '';
	}

	if (e.inactive) {
		return `${e.fullName} (inactive)`;
	}

	return e.fullName;
}

function employeeIsSelectable (e, currentKey) {
	return !e.inactive || (currentKey && e.key === currentKey);
}

function FormattedContractLabel({ contract }: { contract: IJobCost | IChangeOrder }) {
	return <><strong>{ contract.relayNumber} &bull; {contract.accountingId}</strong><br />{contract.name}</>;
}

interface IContractSelectionGroup {
	contract: IJobCost | IChangeOrder, 
	selections: IContractSelection[]
}
function groupContractSelections(selections: IContractSelection[]):  IContractSelectionGroup[] {
	const results:IContractSelectionGroup[] = [];
	
	for(const selection of selections) {
		let resultItem = results.find(item => item.contract === (selection.changeOrder ?? selection.contract));

		if(!resultItem) {
			resultItem = { contract: selection.changeOrder ?? selection.contract, selections: [] };
			results.push(resultItem);
		}

		resultItem.selections.push(selection);
		resultItem.selections = sortBy(resultItem.selections, (selection:IContractSelection) => selection.installSheet?.sortIndex ?? 0);
	}

	return results;
}

function ContractSelectionRow({group, toggleSelected }: { group: IContractSelectionGroup, toggleSelected: (selection: IContractSelection) => void }) {

	if(!group.selections?.length) {
		return null;
	}
	
	if(group.selections.length === 1) {
		const selection = group.selections[0];
		return <label className={ selection.disabled? 'disabled': ''}><input type="checkbox" placeholder="" checked={!!selection.selected}  onChange={ () => { toggleSelected(selection); } }/><FormattedContractLabel contract={group.contract} /></label>;
	}
	else {
		return <Fragment>
			<FormattedContractLabel contract={group.contract} />
			{group.selections.map((selection, index) => <label key={index} className={ selection.disabled? 'disabled': ''}><input type="checkbox" placeholder="" checked={!!selection.selected}  onChange={ () => { toggleSelected(selection); } }/>{ `${index + 1}. ${selection.installSheet?.name}`}</label>)}
		</Fragment>
	}

}

function ContractSelectionModal(
	{ onCancel, onSave, contractSelections, title }: 
	{ onCancel: React.MouseEventHandler<HTMLButtonElement>, onSave: (contractSelections: IContractSelection[])=>void, contractSelections: IContractSelection[], title: string}
) {
	const [state, setState] = useState(() => { return { selections: [...contractSelections.map(cs => { return {...cs} })], hasChanges: false }});
	const selectedCount = state.selections.filter(s => s.selected).length;
	const groups = groupContractSelections(state.selections);

	function toggleSelected(selection: IContractSelection) {
		const newContracts = [...state.selections];
		const updated = newContracts.find(cs => cs.contract.key === selection.contract.key && cs.changeOrder?.key === selection.changeOrder?.key && cs.installSheet?.key === selection.installSheet?.key);

		if(updated && !updated.disabled) {
			updated.selected = !updated.selected;
			const hasChanges = contractSelections.some(c => c.contract.key === updated.contract.key && c.changeOrder?.key === updated.changeOrder?.key && c.installSheet?.key === updated.installSheet?.key && c.selected !== updated.selected);
			setState({ ...state, selections: newContracts, hasChanges });
		}
	}

	return <div className="window">
		<header>
			<button className="cancel" onClick={onCancel}><span>Cancel</span> </button>
			<span>{ title }</span>
			<button className={ [`${selectedCount !== 0 && state.hasChanges? null: 'disabled'}`,'action save'].filter(s => !!s).join(' ') } onClick={ e => onSave(state.selections as IContractSelection[]) }><span>SAVE</span></button>
		</header>
		<div className="views">
			<div className="content in scrollable">
				<section>
					<div className="inner">
						<table className="centered" cellPadding="0" cellSpacing="0">
							<tbody>
								<tr>
									<td></td>
									<td>
										{ groups.map((group, idx) => <ContractSelectionRow key={idx} group={group} toggleSelected={toggleSelected} /> )}
									</td>
									<td></td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>
			</div>
		</div>
	</div>
}

function InstallSheetModal({ jobCost, installSheet, title }: { jobCost: IJobCost, installSheet: IInstallSheet, title: string}) {
	const printReport = () => {
		const { store } = componentServices;
		const job = getModel({ store, collection: ModelCollection.jobs, key: jobCost.jobKey });
		const summary = new JobCostSummary(jobCost, (job && job.getAppliedPaymentTotal(jobCost.key)) || 0);

		return reportActions.fetchReport({ summaries: [summary], job: job, report: `internal/installation/${installSheet.key}` });
	}

	return <div className="window">
		<header><AsyncButton className="icon iconOnly printWhite" asyncAction={printReport} label="" /><span>{ title }</span><button className="close" onClick={ actions.popModal }><span>Close</span></button></header>
		<div className="views">
			<div className="content in scrollable">
				<InstallSheetEdit jobCost={ jobCost } installSheet={ installSheet } />
			</div>
		</div>
	</div>
}

function InstallCompleteModal({ markComplete }: { markComplete: (effectiveDate: Date, description: string) => void }) {
	const [note, setNote] = useState('');
	const [completionDate, setCompletionDate] = useState<Date>(null);

	return <div className="alert">
		<section>
			<header><span>Install Complete</span></header>
			<div className="content">
			This will release the Installer Pay Total minus the 10% holdback and will trigger an inspection by the Field Supervisor.
				<DayPickerInput placeholder="Select Install Completion Date" isVertical={true} selectedDate={ completionDate } onDateSelected={ selected => setCompletionDate(selected.date) } />
				<textarea placeholder="Optional Note" value={note} onChange={ e => setNote(e.target.value) } onBlur={ e => setNote(valueFilters.clean(e.target.value)) }></textarea>
				<div className="actions">
					<button onClick={actions.popModal}><span>Cancel</span></button>
					<button className={ completionDate? 'action': 'action disabled' } onClick={ () => { markComplete(completionDate, note); actions.popModal();} }><span>Mark Complete</span></button>
				</div>
			</div>
		</section>
	</div>
}

function EditInstallCompleteModal({ currentCompletionDate, markComplete }: { currentCompletionDate: Date, markComplete: (effectiveDate: Date, description: string) => void }) {
	const [note, setNote] = useState('');
	const [completionDate, setCompletionDate] = useState<Date>(null);

	return <div className="alert">
		<section>
			<header><span>Edit Install Completion Date</span></header>
			<div className="content">
			This change to the install completion date will no effect on pay, holdback or the project in the Field Supervisor's queue.
				<div><span><strong>Install Completion Date</strong> {util.dateHelper.formatDate(currentCompletionDate, {format: 'MM/dd/yyyy'})}</span></div>
				<DayPickerInput placeholder="Select New Install Completion Date" isVertical={true} selectedDate={ completionDate } onDateSelected={ selected => setCompletionDate(selected.date) } />
				<textarea placeholder="Reason for Adjustment" value={note} onChange={ e => setNote(e.target.value) } onBlur={ e => setNote(valueFilters.clean(e.target.value)) }></textarea>
				<div className="actions">
					<button onClick={actions.popModal}><span>Cancel</span></button>
					<button className={ completionDate? 'action': 'action disabled' } onClick={ e => { markComplete(completionDate, note); actions.popModal();} }><span>Mark Complete</span></button>
				</div>
			</div>
		</section>
	</div>
}

function OverviewRow({ item, setRequiresDelivery, setRequiresInstall }: { item: IOperationsItem, setRequiresDelivery: (IOperationsItem, boolean)=> void, setRequiresInstall: (IOperationsItem, boolean)=> void }) {
	const config = globalConfig.withVariant(item.companyVariant);
	return <tr>
	
		<td></td>
		<td>{ item.relayNumber } &nbsp; <span className="badge" style={{ backgroundColor: config.badge.color}}><span>L</span></span></td>
		<td className='relayNumberAccountingId'>{ item.accountingId }</td>
		<td>{ item.name }</td>
		<td>
			<select className={ item.deliveryDate? 'select disabled': 'select' } value={ item.requiresDelivery? 'yes': 'no' } onChange={e => setRequiresDelivery(item, e.target.value === 'yes')} placeholder="Select">
				<option value="yes">Yes</option>
				<option value="no">No</option>
			</select>
		</td>
		<td className='date'>{ item.deliveryDate? util.dateHelper.formatDate(item.deliveryDate,{ format: 'MM/dd/yyyy' }): '-' }</td>
		<td>
			<select className={ item.installStartDate? 'select disabled': 'select' } value={ item.requiresInstall? 'yes': 'no' } onChange={e => setRequiresInstall(item, e.target.value === 'yes')} placeholder="Select">
				<option value="yes">Yes</option>
				<option value="no">No</option>
			</select>
		</td>
		<td dangerouslySetInnerHTML={ {__html: sortBy(item.installSheets, 'sortIndex').map((s, i) => `${i + 1}. ${s.name}`).join('<br>')}} ></td>
		<td className='date'>{ item.installStartDate? util.dateHelper.formatDate(item.installStartDate,{ format: 'MM/dd/yyyy' }): '-' }</td>
		<td></td>
	</tr>	
}

function OverviewSection({items, setRequiresDelivery, setRequiresInstall }: { items: IOperationsItem[], setRequiresDelivery: (IOperationsItem, boolean)=> void, setRequiresInstall: (IOperationsItem, boolean)=> void }) {

	const OverviewTable = sortableComponent(({ updateSort, sortKey, list }) => {
		return (
			<div>
				<table className="sortable interactive" cellPadding="0" cellSpacing="0">
					<thead>
						<tr>
							<td></td>
							<td className={ sortKey === 'relayNumber'? 'sorted': undefined } onClick={ () => updateSort('relayNumber') }>Relay No.</td>
							<td className={ sortKey === 'accountingId'? 'relayNumberAccountingId sorted': 'relayNumberAccountingId' } onClick={ () => updateSort('accountingId') }>Legacy ID</td>
							<td className={ sortKey === 'name'? 'sorted': undefined } onClick={ () => updateSort('name') }>Name</td>
							<td className={ sortKey === 'requiresDelivery'? 'sorted': undefined } onClick={ () => updateSort('requiresDelivery') }>Requires Delivery?</td>
							<td className={ sortKey === 'deliveryDate'? 'date sorted': 'date' } onClick={ () => updateSort('deliveryDate') }>Delivery Date</td>
							<td className={ sortKey === 'requiresInstall'? 'sorted': undefined } onClick={ () => updateSort('requiresInstall') }>Requires Install?</td>
							<td>Install Sheet</td>
							<td className={ sortKey === 'installStartDate'? 'date sorted': 'date' } onClick={ () => updateSort('installStartDate') }>Install Start Date</td>
							<td></td>
						</tr>
					</thead>
					<tbody>
						{ list.map((item: IOperationsItem, idx: number) => <OverviewRow key={ idx } item={ item } setRequiresDelivery={ setRequiresDelivery } setRequiresInstall={ setRequiresInstall } />) }
					</tbody>
				</table>
			</div>
		);
	});

	return <Expander title="Overview" contentClassNames={['inner']}><OverviewTable list={ items } /></Expander>
}

interface IBucketProps {
	contracts: IJobCost[];
	visits: ISiteVisit[];
	addVisit(type: SiteVisitType, selections: IContractSelection[], callback?: () => void): void;
	updateSelectedContracts(siteVisitKey: string, selections: IContractSelection[], callback?: () => void): void;
	deleteVisit(siteVisitKey: string, callback?: () => void): void;
	setPrimaryEmployee(siteVisitKey: string, employeeKey: string): void;
	setScheduledDate(siteVisitKey: string, date: Date): void;
	setVisitStringAt(e: { type: string, target: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement }, siteVisitKey: string, path: string): void;
	updateContracts?: (updater: (contracts: IJobCost[]) => void, options: { autoSave: boolean}) => void
	setVisitStatus?: (siteVisitKey: string, status: SiteVisitStatus, effectiveDate: Date, description: string) => void;
}

function DeliveryBucket({ contracts, visits, deliveryCompanies, addVisit, updateSelectedContracts, deleteVisit, setScheduledDate, setVisitStringAt }: IBucketProps & { deliveryCompanies: IContractor[] }) {
	const addDelivery = useCallback((selections: IContractSelection[]) => {
		addVisit(SiteVisitType.delivery, selections, () => {
			actions.deleteModal({ key: OPS_MODAL_KEY })
		});
	}, [addVisit]);

	const onAddClick = useCallback(e => {
		const selections = componentServices.operationsHelper.getContractSelectionsForVisit(contracts, visits, SiteVisitType.delivery);

		actions.pushModal({
			key: OPS_MODAL_KEY,
			component: <ContractSelectionModal title="New Delivery" contractSelections={ selections } onSave={ addDelivery } onCancel={ e => actions.deleteModal({ key: OPS_MODAL_KEY })} />
		});

	}, [contracts, visits]);

	const onEditClick = useCallback((siteVisitKey: string) => {
		const delivery = visits.find(d => d.key === siteVisitKey);
		const selections:IContractSelection[] = componentServices.operationsHelper.getContractSelectionsForVisit(contracts, visits, SiteVisitType.delivery, delivery);

		function editDelivery(selections: IContractSelection[]) {
			updateSelectedContracts(siteVisitKey, selections, () => {
				actions.deleteModal({ key: OPS_MODAL_KEY });
			});
		}
		actions.popModal();
		actions.pushModal({
			key: OPS_MODAL_KEY,
			component: <ContractSelectionModal title="Edit Delivery" contractSelections={ selections } onSave={ editDelivery } onCancel={ e => actions.deleteModal({ key: OPS_MODAL_KEY })}/>
		});
		
	}, [contracts, visits, updateSelectedContracts]);

	const confirmDelete = useCallback((siteVisitKey: string) => {
		const popup = <Confirm title={ `Permanently Delete Delivery?` } message={ `Are you sure you want to permanently delete this Delivery? This cannot be undone.` } confirmLabel="Permanently Delete" cancelLabel="Cancel" cancelHandler={ actions.popModal } confirmHandler={ () => deleteVisit(siteVisitKey, actions.popModal) } />;

		actions.popModal();
		actions.pushModal({ component: popup });
	}, []);

	const onContextMenuClick = useCallback((e, siteVisitKey: string) => {
		const items = [{ 
			label: 'Edit Delivery', 
			className: 'editItem', 
			action: () => onEditClick(siteVisitKey)
		},{ 
			label: 'Delete', 
			className: 'removeItem delete', 
			action: () => confirmDelete(siteVisitKey)
		}]
		const popup = <Popup target={e.target}><ContextMenu items={ items } /></Popup>;

		actions.pushModal({ component: popup });
	}, []);

	return <Expander title="Delivery" actions={<button className="icon iconOnly addItemWhite" onClick={onAddClick}><span></span></button>}>
		{
			visits.map(d => {
				const { currentStatus } = d;
				const titleClassNames = ['icon'];

				if(!d.scheduledDate) {
					titleClassNames.push('scheduleNone red');
				}
				else {
					switch(currentStatus) {
					case SiteVisitStatus.completed:
						titleClassNames.push('scheduleComplete');
						break;
					default:
						titleClassNames.push('schedule');
						break;
					}
				}
				let includedContracts: (IJobCost | IChangeOrder)[] = contracts.filter(c => some(d.jobCosts, jc => jc.jobCostKey === c.key && !jc.changeOrderKey));
				includedContracts = includedContracts.concat(contracts.flatMap(c => c.changeOrders.toArray().filter(co => some(d.jobCosts, jc => jc.changeOrderKey === co.key)))).sort((a, b) => Number(a.relayId) - Number(b.relayId));

				return <Expander 
					key={ d.key } 
					title={ d.scheduledDate? util.dateHelper.formatDate(d.scheduledDate, { format: 'MM/dd/yyyy' }): 'No Date' } 
					titleClassNames={titleClassNames}
					subTitle={ d.deliverer || 'No Deliverer Assigned'} 
					subtitleClassNames={ d.deliverer? []: ['red'] }
					contentClassNames={['inner', 'clearfix']} 
					actions={ <button className="icon iconOnly contextMenu" onClick={e => onContextMenuClick(e, d.key)}><span></span></button> }>
					<table className="half" cellPadding="0" cellSpacing="0">
						<tbody>
							<tr className={ d.isValid('scheduledDate')? undefined: 'required' }>
								<td></td>
								<td><strong>Delivery Date</strong></td>
								<td className="date">
									<DayPickerInput selectedDate={ d.scheduledDate } isVertical={ true } onDateSelected={ selected => setScheduledDate(d.key, selected.date) } restrictedDates={ [] }/>
								</td>
								<td></td>
							</tr>
							<tr>
								<td></td>
								<td><strong>Deliver Contracts</strong></td>
								<td>{ includedContracts.map(c => <span key={c.key} className="readOnly"><FormattedContractLabel contract={c} /></span>) }</td>
								<td></td>
							</tr>
						</tbody>
					</table>
					<table className='half' cellPadding="0" cellSpacing="0">
						<tbody>
							<tr>
								<td></td>
								<td><strong>Who is Delivering?</strong></td>
								<td><select placeholder="Who is Deliverying?" value={ d.viewValue('deliverer') } onChange={ e => setVisitStringAt( e, d.key, '/deliverer') }>
									<option>Select</option>
									{
										deliveryCompanies.map(c => <option key={c.key} value={c.key}>{ c.name }</option>)
									}</select></td>
								<td></td>
							</tr>
							<tr>
								<td></td>
								<td><strong>Delivery Instructions</strong></td>
								<td><textarea placeholder="Delivery Instructions" value={ d.viewValue('/instructions') } onChange={ e => setVisitStringAt(e, d.key, '/instructions')} onBlur={ e => setVisitStringAt(e, d.key, '/instructions')}></textarea></td>
								<td></td>
							</tr>
						</tbody>
					</table>
				</Expander>})
		}
		
	</Expander>;
}

interface IInstallerPayContract {
	contract: IJobCost,
	visit: ISiteVisit;
	items: IInstallerPayContractItem[],
	installTotal: number;
}

interface IInstallerPayContractItem {
	installSheet: IInstallSheet,
	groups: { title: string, amount: number } [],
	adjustmentItems: IJobCostLineItem[],
	subTotal: number;
}

type InstallerPayItemUpdater = (updateItemAt: (key: string, path: string, value: string) => void, addItem: (contractKey: string, item: IJobCostLineItem) => void, deleteItem:(key: string) => void) => void;
type UpdateItemsHandler = (updater: InstallerPayItemUpdater, options: {autoSave: boolean}) => void;


function InstallerPayContractItemRow({ payContractItem, contract, updateItems, index, visit } : { payContractItem: IInstallerPayContractItem, contract: IJobCost, updateItems: UpdateItemsHandler, index: number, visit: ISiteVisit }) {
	const createPendingItem = () => {
		return jobCostLineItemFactory({ type: 'installation-adjustment', installSheetKey: payContractItem.installSheet.key });
	}
	const [pendingItem, setPendingItem] = useState<IJobCostLineItem>(createPendingItem);
	const [shouldValidateAdd, setShouldValidateAdd] = useState(false);
	const { adjustmentItems, groups, installSheet, subTotal } = payContractItem;
	const addItemHandler = useCallback(() => {
		updateItems((updateAt, addItem ) => {
			if(pendingItem.isValid() && pendingItem.description && pendingItem.cost) {
				addItem(contract.key, pendingItem);
				setPendingItem(createPendingItem());
				setShouldValidateAdd(false);
			}
			else {
				setShouldValidateAdd(true);
			}
		}, { autoSave: true });
	}, [updateItems, pendingItem, contract]);
	const updateItemHandler = useCallback((key: string, path: string, e) => {
		const val = e.type === 'blur'? valueFilters.clean(e.target.value): e.target.value;
		
		if(key === pendingItem.key) {
			const item = jobCostLineItemFactory(pendingItem);

			item.setAt(path, val);
			setPendingItem(item);
		}
		else {
			const autoSave = e.type === 'blur';

			updateItems((updateAt) => {
				updateAt(key, path, val);
			}, { autoSave });
		} 
	}, [updateItems, pendingItem, adjustmentItems]);

	const onDuplicateClick = useCallback((key: string) => {
		const existing = contract && contract.lineItems[key];
		
		if(existing) {
			updateItems((updateAt, addItem) => {
				const duplicate = existing.clone();
				addItem(contract.key, duplicate);
				actions.popModal();
			}, {autoSave: true})
		}
		
	}, [updateItems, contract]);

	const deleteAdjustment = useCallback((key: string) => {
		updateItems((updateAt, addItem, deleteItem) => {
			deleteItem(key);
			actions.popModal();
		}, { autoSave: true});
	}, [updateItems, adjustmentItems])

	const confirmDelete = useCallback((key: string) => {
		const popup = <Confirm title={ `Permanently Delete Adjustment?` } message={ `Are you sure you want to permanently delete this Pay Adjustment? This cannot be undone.` } confirmLabel="Permanently Delete" cancelLabel="Cancel" cancelHandler={ actions.popModal } confirmHandler={ () => deleteAdjustment(key) } />;

		actions.popModal();
		actions.pushModal({ component: popup });
	}, []);


	const onContextMenuClick = useCallback((e, key: string) => {
		const items = [{ 
			label: 'Duplicate', 
			className: 'duplicateItem', 
			action: () => onDuplicateClick(key)
		},{ 
			label: 'Delete', 
			className: 'removeItem delete', 
			action: () => confirmDelete(key)
		}]
		const popup = <Popup target={e.target}><ContextMenu items={ items } /></Popup>;

		actions.pushModal({ component: popup });
	}, []);

	return <table className='installSheetTable' cellPadding='0' cellSpacing='0'>
		<tbody>
			<tr>
				<td className='installSheet'><span className='readOnly'><strong>{index + 1}. {payContractItem.installSheet.name}</strong></span></td>
				<td className='innerTable'>
					<table className='compact' cellPadding='0' cellSpacing='0'>
						<tbody>
							{ visit.completedDate? null: <tr>
								<td></td>
								<td className={ shouldValidateAdd && !pendingItem.description? 'category required': 'category' }>
									<input type="text" placeholder="Adjustment - Enter a Description" autoCapitalize="" autoCorrect="off" value={ pendingItem.viewValue('description') } onChange={ e => updateItemHandler(pendingItem.key, 'description', e) } onBlur={ e => updateItemHandler(pendingItem.key, 'description', e) } />
								</td>
								<td className={ shouldValidateAdd && !pendingItem.cost? 'price required': 'price' }><CurrencyInput type="text" placeholder="0" 
									value={pendingItem.viewValue('cost')} 
									onChange={e => updateItemHandler(pendingItem.key, 'cost', e)} 
									onBlur={e => updateItemHandler(pendingItem.key, 'cost', e)}/></td>
								<td className='actions'><button className='icon iconOnly addItem' onClick={ addItemHandler }><span></span></button></td>
								<td></td>
							</tr>
							}
							{ 
								adjustmentItems.map(item => <tr key={ item.key } className="added">
									<td></td>
									<td className={ shouldValidateAdd && !item.isValid('description')? 'category required': 'category' }>
										<input type="text" placeholder="Enter a Description" autoCapitalize="" autoCorrect="off" value={ item.viewValue('description') } onChange={ e => updateItemHandler(item.key, 'description', e) } onBlur={ e => updateItemHandler(item.key, 'description', e) } />
									</td>
									<td className="price"><CurrencyInput type="text" placeholder="0" 
										value={item.viewValue('cost')} 
										onChange={e => updateItemHandler(item.key, 'cost', e)} 
										onBlur={e => updateItemHandler(item.key, 'cost', e)}/></td>
									<td><button className="icon iconOnly contextMenu" onClick={ e=> onContextMenuClick(e, item.key)}><span></span></button></td>
									<td></td>
								</tr>)
							}
							{
								groups.map(g => <tr key={ `${installSheet.key}-${g.title}` } className="added">
									<td></td>
									<td className='category'><span className="readOnly">{ g.title }</span></td>
									<td className="price"><span className="readOnly">{util.formatAsCurrency(g.amount)}</span></td>
									<td><button className="icon iconOnly openWindow" onClick={e => actions.pushModal({ component: <InstallSheetModal jobCost={ contract } installSheet={ installSheet } title={ installSheet.name } />}) }><span></span></button></td>
									<td></td>
								</tr>)
							}
						</tbody>
						<tfoot className='subtotal'>
							<tr>
								<td></td>
								<td className='right'>Subtotal</td>
								<td className='price'><span className='readOnly'>{util.formatAsCurrency(subTotal)}</span></td>
								<td className='actions'><button className='icon iconOnly openWindow' onClick={e => actions.pushModal({ component: <InstallSheetModal jobCost={ contract } installSheet={ installSheet } title={ installSheet.name } />}) }><span></span></button></td>
								<td></td>
							</tr>
						</tfoot>
					</table>
				</td>
			</tr>
		</tbody>
	</table>
}

function InstallerPayContractRow({ payContract, updateItems } : { payContract: IInstallerPayContract, updateItems: UpdateItemsHandler }) {
	const { contract, items, visit } = payContract;

	return <tr className='contractRow'>
		<td></td>
		<td className='contract'><span className='readOnly'><FormattedContractLabel contract={contract} /></span></td>
		<td className='innerTable' colSpan={4}>
			{
				items.map((item, index) => <InstallerPayContractItemRow key={item.installSheet.key} visit={visit} contract={contract} payContractItem={item} updateItems={updateItems} index={index} />)
			}
		</td>
		<td></td>
	</tr>
}

function InstallerPayContractTable({ visit, contracts, installSheetKeys, configs, adjustmentItems, updateItems }: { visit: ISiteVisit, contracts: IJobCost[], installSheetKeys: string[], configs: Record<string, any>, adjustmentItems: IJobCostLineItem[], updateItems: UpdateItemsHandler}) {
	let totalPay = 0;
	const payContracts:IInstallerPayContract[]  = contracts.map(contract => {
		const installSheets = sortBy(getInstallSheets(contract).toArray().filter(sheet => installSheetKeys.indexOf(sheet.key) !== -1), 'sortIndex');
		const { installConfigName } = actions.getInstallationConfig(contract);
		const configGroups = configs[installConfigName]?.groups;
		const result = { contract, visit, items: [], installTotal: 0 };

		for(const installSheet of installSheets) {
			const lineItems = contract.getLineItemsByType(['installation', 'installation-custom', 'travel']).filter(li => li.installSheetKey === installSheet.key);
			const sheetAdjustments = adjustmentItems.filter(i => i.installSheetKey === installSheet.key);
			const subTotal = lineItems.reduce((sum, li) => sum + Number(li.cost) * li.quantity, sheetAdjustments.reduce((sum, i) => sum + Number(i.cost) * i.quantity, 0));
			const groups = !configGroups? []: configGroups.map(g => {
				return {
					title: g.name,
					amount: lineItems.filter(li => g.tests.some(t => t(li))).reduce((sum, li) => sum + Number(li.cost) * li.quantity, 0)
				};
			});

			result.items.push({
				installSheet,
				adjustmentItems: sheetAdjustments,
				groups,
				subTotal
			});

			result.installTotal += subTotal;
			totalPay += subTotal;
		}

		return result;
	});
	
	// totalPay = adjustmentItems.reduce((sum, i) => sum + Number(i.cost) * i.quantity, totalPay);
	
	return <table className='installerPay' cellPadding='0' cellSpacing='0'>
		<thead>
			<tr>
				<td></td>
				<td className='contract'>Contract</td>
				<td className='installSheet'><span className='readOnly'>Install Sheet</span></td>
				<td className='category'><span className='readOnly'>Category</span></td>
				<td className='price'><span className='readOnly'>Amount</span></td>
				<td className='actions'></td>
				<td></td>
			</tr>
		</thead>
		<tbody>
			{
				payContracts.map(c => <InstallerPayContractRow key={c.contract.key} payContract={c} updateItems={updateItems} />)
			}
			
		</tbody>
		<tfoot className='total'>
			<tr>
				<td></td>
				<td></td>
				<td></td>
				<td className='right'>Installer Pay Total</td>
				<td className='cost'><span className='readOnly'>{ util.formatAsCurrency(totalPay)}</span></td>
				<td className='actions'></td>
				<td></td>
			</tr>
		</tfoot>
	</table>
}

// function InstallerPayTableOld({ contracts, configs, adjustmentItems, updateItems }: { contracts: IJobCost[], configs: Record<string, any>, adjustmentItems: IJobCostLineItem[], updateItems: (updater: InstallerPayItemUpdater, options: {autoSave: boolean}) => void }) {
// 	const installBudgets = contracts.flatMap(c => c.installSheets.toArray().map(i => {
// 		const installSheetCount = c.installSheets.length();
// 		const lineItems = c.getLineItemsByType(['installation', 'installation-custom', 'travel']).filter(li => li.installSheetKey === i.key);
// 		const { installConfigName } = actions.getInstallationConfig(c);
// 		const configGroups = configs[installConfigName]?.groups;
// 		const groups = !configGroups? []: configGroups.map(g => {
// 			return {
// 				title: installSheetCount > 1? `${i.name} • ${g.name}`: g.name,
// 				amount: lineItems.filter(li => g.tests.some(t => t(li))).reduce((sum, li) => sum + Number(li.cost) * li.quantity, 0)
// 			};
// 		})

// 		return {
// 			contract: c,
// 			installSheet: i,
// 			groups,
// 			totalInstall: lineItems.reduce((sum, li) => sum + Number(li.cost) * li.quantity, 0)
// 		} 
// 	}));
// 	const totalPay = installBudgets.reduce((sum, b) => sum + b.totalInstall, adjustmentItems.reduce((sum, i) => sum + Number(i.cost) * i.quantity, 0));
// 	const createPendingItem = () => {
// 		return jobCostLineItemFactory({ type: 'installation-adjustment' });
// 	}
// 	const [pendingItem, setPendingItem] = useState<IJobCostLineItem>(createPendingItem);
// 	const [pendingContractKey, setPendingContractKey] = useState('');
// 	const [shouldValidateAdd, setShouldValidateAdd] = useState(false);
// 	const addItemHandler = useCallback(() => {
// 		updateItems((updateAt, addItem ) => {
// 			if(pendingContractKey && pendingItem.isValid()) {
// 				addItem(pendingContractKey, pendingItem);
// 				setPendingItem(createPendingItem());
// 				setPendingContractKey('');
// 				setShouldValidateAdd(false);
// 			}
// 			else {
// 				setShouldValidateAdd(true);
// 			}
// 		}, { autoSave: true });
// 	}, [updateItems, pendingItem, pendingContractKey]);
// 	const updateItemHandler = useCallback((key: string, path: string, e) => {
// 		const val = e.type === 'blur'? valueFilters.clean(e.target.value): e.target.value;
		
// 		if(key === pendingItem.key) {
// 			const item = jobCostLineItemFactory(pendingItem);

// 			item.setAt(path, val);
// 			setPendingItem(item);
// 		}
// 		else {
// 			const autoSave = e.type === 'blur';

// 			updateItems((updateAt) => {
// 				updateAt(key, path, val);
// 			}, { autoSave });
// 		} 
// 	}, [updateItems, pendingItem, adjustmentItems]);

// 	const onDuplicateClick = useCallback((key: string) => {
// 		const contract = contracts.find(c => !!c.lineItems[key]);
// 		const existing = contract && contract.lineItems[key];
		
// 		if(existing) {
// 			updateItems((updateAt, addItem) => {
// 				const duplicate = existing.clone();
// 				addItem(contract.key, duplicate);
// 				actions.popModal();
// 			}, {autoSave: true})
// 		}
		
// 	}, [updateItems, contracts]);

// 	const deleteAdjustment = useCallback((key: string) => {
// 		updateItems((updateAt, addItem, deleteItem) => {
// 			deleteItem(key);
// 			actions.popModal();
// 		}, { autoSave: true});
// 	}, [updateItems, adjustmentItems])

// 	const confirmDelete = useCallback((key: string) => {
// 		const popup = <Confirm title={ `Permanently Delete Adjustment?` } message={ `Are you sure you want to permanently delete this Pay Adjustment? This cannot be undone.` } confirmLabel="Permanently Delete" cancelLabel="Cancel" cancelHandler={ actions.popModal } confirmHandler={ () => deleteAdjustment(key) } />;

// 		actions.popModal();
// 		actions.pushModal({ component: popup });
// 	}, []);


// 	const onContextMenuClick = useCallback((e, key: string) => {
// 		const items = [{ 
// 			label: 'Duplicate', 
// 			className: 'duplicateItem', 
// 			action: () => onDuplicateClick(key)
// 		},{ 
// 			label: 'Delete', 
// 			className: 'removeItem delete', 
// 			action: () => confirmDelete(key)
// 		}]
// 		const popup = <Popup target={e.target}><ContextMenu items={ items } /></Popup>;

// 		actions.pushModal({ component: popup });
// 	}, []);

// 	return <table className="compact installerPay" cellPadding="0" cellSpacing="0">
// 		<thead>
// 			<tr>
// 				<td></td>
// 				<td><span className="readOnly">Contract</span></td>
// 				<td><span className="readOnly">Installer Pay</span></td>
// 				<td className="price"><span className="readOnly">Amount</span></td>
// 				<td></td>
// 				<td></td>
// 			</tr>
// 		</thead>
// 		<tbody>
// 			<tr>
// 				<td></td>
// 				<td className={ shouldValidateAdd && !pendingContractKey? 'relayNumberAccountingIdFriendlyName required': 'relayNumberAccountingIdFriendlyName' }><select value={ pendingContractKey } onChange={ e => setPendingContractKey(e.target.value)}><option>Select</option>
// 					{
// 						contracts.map(c => <option key={ c.key } value={ c.key }><FormattedContractLabel contract={c} /></option>)
// 					}
// 				</select></td>
// 				<td className={ shouldValidateAdd && !pendingItem.isValid('description')? 'required': '' }>
// 					<input type="text" placeholder="Adjustment - Enter a Description" autoCapitalize="" autoCorrect="off" value={ pendingItem.viewValue('description') } onChange={ e => updateItemHandler(pendingItem.key, 'description', e) } onBlur={ e => updateItemHandler(pendingItem.key, 'description', e) } />
// 				</td>
// 				<td className={ shouldValidateAdd && !pendingItem.isValid('cost')? 'price required': 'price' }><CurrencyInput type="text" placeholder="0" 
// 					value={pendingItem.viewValue('cost')} 
// 					onChange={e => updateItemHandler(pendingItem.key, 'cost', e)} 
// 					onBlur={e => updateItemHandler(pendingItem.key, 'cost', e)}/></td>
// 				<td><button className="icon iconOnly addItem" onClick={ addItemHandler }><span></span></button></td>
// 				<td></td>
// 			</tr>
// 			{ 
// 				adjustmentItems.map(item => <tr key={ item.key } className="added">
// 					<td></td>
// 					<td className="relayNumberAccountingIdFriendlyName"><span className="readOnly"><FormattedContractLabel contract={contracts.find(c => !!c.lineItems.get(item.key))} /></span></td>
// 					<td className={ shouldValidateAdd && !item.isValid('description')? 'required': '' }>
// 						<input type="text" placeholder="Adjustment - Enter a Description" autoCapitalize="" autoCorrect="off" value={ item.viewValue('description') } onChange={ e => updateItemHandler(item.key, 'description', e) } onBlur={ e => updateItemHandler(item.key, 'description', e) } />
// 					</td>
// 					<td className="price"><CurrencyInput type="text" placeholder="0" 
// 						value={item.viewValue('cost')} 
// 						onChange={e => updateItemHandler(item.key, 'cost', e)} 
// 						onBlur={e => updateItemHandler(item.key, 'cost', e)}/></td>
// 					<td><button className="icon iconOnly contextMenu" onClick={ e=> onContextMenuClick(e, item.key)}><span></span></button></td>
// 					<td></td>
// 				</tr>)
// 			}
// 			{
// 				installBudgets.flatMap(b => b.groups.map(g => <tr key={ b.installSheet.key } className="added">
// 					<td></td>
// 					<td className="relayNumberAccountingIdFriendlyName"><span className="readOnly"><FormattedContractLabel contract={b.contract} /></span></td>
// 					<td><span className="readOnly">{ g.title }</span></td>
// 					<td className="price"><span className="readOnly">{util.formatAsCurrency(g.amount)}</span></td>
// 					<td><button className="icon iconOnly openWindow" onClick={e => actions.pushModal({ component: <InstallSheetModal jobCost={ b.contract } installSheet={ b.installSheet } title={ b.installSheet.name } />}) }><span></span></button></td>
// 					<td></td>
// 				</tr>))
// 			}
// 		</tbody>
// 		<tfoot className="subtotal">
// 			<tr>
// 				<td></td>
// 				<td></td>
// 				<td className="right">Installer Pay Total</td>
// 				<td className="cost"><span className="readOnly">{ util.formatAsCurrency(totalPay) }</span></td>
// 				<td></td>
// 				<td></td>
// 			</tr>
// 		</tfoot>
// 	</table>
// }

function IntstallBucket({ contracts, installConfigs, visits, addVisit, updateContracts, setVisitStatus, updateSelectedContracts, deleteVisit, setPrimaryEmployee, setScheduledDate, setVisitStringAt }: IBucketProps & { installConfigs: any}) {
	const installers = useMemo(() => {
		const employees = getCollection({ store: componentServices.store, collection: ModelCollection.employees });

		return employees.filter(e => e.orgTitleKey === 'installer').toArray();
	}, [componentServices.store]);

	const addInstall = useCallback((selections: IContractSelection[]) => {
		addVisit(SiteVisitType.install, selections, () => {
			actions.deleteModal({ key: OPS_MODAL_KEY })
		});
	}, [addVisit]);

	const onAddClick = useCallback(e => {
		const selections = componentServices.operationsHelper.getContractSelectionsForVisit(contracts, visits, SiteVisitType.install);

		actions.pushModal({
			key: OPS_MODAL_KEY,
			component: <ContractSelectionModal title="New Install" contractSelections={ selections } onSave={ addInstall } onCancel={ e => actions.deleteModal({ key: OPS_MODAL_KEY })} />
		});

	}, [contracts, visits]);

	const onEditClick = useCallback((siteVisitKey: string) => {
		const install = visits.find(d => d.key === siteVisitKey);
		const selections = componentServices.operationsHelper.getContractSelectionsForVisit(contracts, visits, SiteVisitType.install, install);

		function editInstall(selections: IContractSelection[]) {
			updateSelectedContracts(siteVisitKey, selections, () => {
				actions.deleteModal({ key: OPS_MODAL_KEY });
			});
		}
		actions.popModal();
		actions.pushModal({
			key: OPS_MODAL_KEY,
			component: <ContractSelectionModal title="Edit Install" contractSelections={ selections } onSave={ editInstall } onCancel={ e => actions.deleteModal({ key: OPS_MODAL_KEY })} />
		});
		
	}, [contracts, visits, updateSelectedContracts]);

	const confirmDelete = useCallback((siteVisitKey: string) => {
		const popup = <Confirm title={ `Permanently Delete Install?` } message={ `Are you sure you want to permanently delete this Install? This cannot be undone.` } confirmLabel="Permanently Delete" cancelLabel="Cancel" cancelHandler={ actions.popModal } confirmHandler={ () => deleteVisit(siteVisitKey, actions.popModal) } />;

		actions.popModal();
		actions.pushModal({ component: popup });
	}, []);

	const onContextMenuClick = (e, siteVisitKey: string) => {
		const items = [{ 
			label: 'Edit Install', 
			className: 'editItem', 
			action: () => onEditClick(siteVisitKey)
		},{ 
			label: 'Delete', 
			className: 'removeItem delete', 
			action: () => confirmDelete(siteVisitKey)
		}]
		const popup = <Popup target={e.target}><ContextMenu items={ items } /></Popup>;

		actions.pushModal({ component: popup });
	};

	const updateInstallerPayItems = useCallback((updater:InstallerPayItemUpdater, options: { autoSave: boolean}) => {
		updateContracts(contracts => {
			updater((key, path, value) => {
				const existing = contracts.flatMap(c => c.lineItems.toArray().filter(li => li.key === key));
				if(existing.length) {
					existing[0].setAt(path, value);
				}
			}, (contractKey, item) => {
				const contract = contracts.find(c => c.key === contractKey);
				if(contract) {
					contract.lineItems.add(item);
				}
			}, (key) => {
				const contract = contracts.find(c => !!c.lineItems[key]);
				if(contract) {
					contract.lineItems.delete(key);
				}
			});
		}, options);
	}, [updateContracts, contracts, visits]);

	const onMarkComplete = useCallback((visit: ISiteVisit) => {
		function markComplete(effectiveDate: Date, description: string) {
			setVisitStatus(visit.key, SiteVisitStatus.completed, effectiveDate, description);
		}
		actions.pushModal({ 
			component: <InstallCompleteModal markComplete={ markComplete } />
		})
	}, [setVisitStatus, visits]);

	const onEditCompletionDate = useCallback((visit: ISiteVisit) => {
		function markComplete(effectiveDate: Date, description: string) {
			setVisitStatus(visit.key, SiteVisitStatus.completed, effectiveDate, description);
		}
		actions.pushModal({ 
			component: <EditInstallCompleteModal currentCompletionDate={visit.completedDate} markComplete={ markComplete } />
		})
	}, [setVisitStatus, visits]);

	return <Expander title="Install" actions={<button className="icon iconOnly addItemWhite" onClick={onAddClick}><span></span></button>}>
		{
			visits.map(v => {
				const rootContracts = contracts.filter(c => some(v.jobCosts, jc => jc.jobCostKey === c.key && !jc.changeOrderKey)).map(jc => contractProxy(jc, undefined, true));
				const includedContracts: (IJobCost)[] = [].concat(rootContracts, contracts.flatMap(c => c.changeOrders.toArray().filter(co => some(v.jobCosts, jc => jc.changeOrderKey === co.key)).map(co => contractProxy(c, co, true)))).sort((a, b) => Number(a.relayId) - Number(b.relayId));
				const currentStatus = v.currentStatus;
				const assignedInstaller = installers?.find(i => i.key === v.primaryEmployee?.employeeKey);
				const titleClassNames = ['icon'];

				if(!v.scheduledDate) {
					titleClassNames.push('scheduleNone red');
				}
				else {
					switch(currentStatus) {
					case SiteVisitStatus.completed:
						titleClassNames.push('scheduleComplete');
						break;
					default:
						titleClassNames.push('schedule');
						break;
					}
				}
				return <Expander 
					key={ v.key } 
					title={ v.scheduledDate? util.dateHelper.formatDate(v.scheduledDate, { format: 'MM/dd/yyyy' }): 'No Date' } 
					titleClassNames={titleClassNames} 
					subTitle={ assignedInstaller? formatEmployeeDisplayName(assignedInstaller): 'No Installer Assigned'} 
					subtitleClassNames={ assignedInstaller? []: ['red'] }
					contentClassNames={['inner', 'clearfix']} 
					actions={ <button className="icon iconOnly contextMenu" onClick={e => onContextMenuClick(e, v.key)}><span></span></button> }
				>
					<table className='half' cellPadding="0" cellSpacing="0">
						<tbody>
							<tr className={ v.isValid('scheduledDate')? undefined: 'required' }>
								<td></td>
								<td><strong>Install Start Date</strong></td>
								<td>
									{v.completedDate? util.dateHelper.formatDate(v.scheduledDate, { format: 'MM/dd/yyyy' }):
										<DayPickerInput selectedDate={ v.scheduledDate } isVertical={ true } onDateSelected={ selected => setScheduledDate(v.key, selected.date) } restrictedDates={ [] } />
									}
								</td>
								<td></td>
							</tr>
							{ !v.completedDate? null: <tr>
								<td></td>
								<td><strong>Install Completion Date</strong></td>
								<td><button className="icon iconLabel iconRight editCell" onClick={() => onEditCompletionDate(v)}><span>{util.dateHelper.formatDate(v.completedDate, { format: 'MM/dd/yyyy' })}</span></button></td>
								<td></td>
							</tr> }
						</tbody>
					</table>
					<table className='half' cellPadding="0" cellSpacing="0">
						<tbody>
							<tr>
								<td></td>
								<td><strong>Installer</strong></td>
								<td>{ !v.completedDate? <select placeholder="Installer" value={ v.primaryEmployee?.employeeKey } onChange={ e => setPrimaryEmployee(v.key, e.target.value )}>
									<option key="" value="">Select</option>
									{
										installers.filter(i => employeeIsSelectable(i, v.primaryEmployee?.employeeKey)).map(i => <option key={i.key} value={i.key}>{formatEmployeeDisplayName(i)}</option>)
									}</select> : formatEmployeeDisplayName(assignedInstaller)
								}</td>
								<td></td>
							</tr>
							<tr>
								<td></td>
								<td><strong>Install Notes</strong></td>
								<td>{ v.viewValue('/instructions') }</td>
								<td></td>
							</tr>
						</tbody>
						{ v.completedDate? null: 
							<table className='installNotes' cellPadding='0' cellSpacing='0'>
								<tbody>
									<tr>
										<td></td>
										<td><strong>Install Notes</strong></td>
										<td><textarea placeholder="Install Notes" value={ v.viewValue('/instructions') } onChange={ e => setVisitStringAt(e, v.key, '/instructions')} onBlur={ e => setVisitStringAt(e, v.key, '/instructions')}></textarea></td>
										<td></td>
									</tr>
								</tbody>
							</table>}
					</table>
					<InstallerPayContractTable visit={v} contracts={ includedContracts } installSheetKeys={v.jobCosts.toArray().map(jc => jc.installSheetKey)} configs={installConfigs} adjustmentItems={ includedContracts.flatMap(c => c.lineItems.toArray().filter(li => li.type === 'installation-adjustment')) } updateItems={ updateInstallerPayItems } />
					<div className="actions prominent">
						<button className={ `action iconLabel markComplete${v.currentStatus === SiteVisitStatus.completed? ' disabled': ''}`} onClick={ () => onMarkComplete(v) }><span>Mark Install Complete</span></button>
					</div>
				</Expander>})
		}
		
	</Expander>;
}

function HistorySections({ contracts, emptyTitle }: { contracts: IJobCost[], emptyTitle: string }) {
	const changeOrders = contracts.flatMap(c => c.changeOrders.toArray());
	const sections = map(sortBy([].concat(contracts, changeOrders), c => {
		const sortStatus = c.getMostRecentStatusByStatus(['approved', 'pending-approval']);
		
		return sortStatus ? sortStatus.dateCreated : c.dateCreated;
	}), c => {
		return <HistorySection emptyTitle = {emptyTitle} contract={ c } key={ c.key } />;
	});

	if (!sections) {
		return null;
	}

	return (
		<Expander title="History" >
			{ sections }
		</Expander>);
}

export default function JobOperationsEdit({ job, store }: Partial<IJobEditChildProps>) {
	const [saveRoot] = useSaveRoot<IOperationsModelFacade>({
		autoSave:  true,
		globalSave: true,
		warnOnLeave: true,
		stateId: `JobOperationsEdit-${job?.key}`,
		modelFactory: operationsModelFacadeFactory,
		resolveMutableModel(current: IOperationsModelFacade | null) {
			const contracts = getCollection({ collection: ModelCollection.jobCosts }).filter(jc => jc.jobKey === job?.key && jc.isContract);
			const visits = getCollection({ collection: ModelCollection.siteVisits }).filter(sv => sv.jobKey === job?.key);
			let mutable = current;
			const rebase = mutable && mutable.needsRebase(job, contracts, visits);

			if(job && (!mutable || rebase)) {
				const mutableVisits = visits.toArray();

				if(rebase) {
					// be sure we don't lose any visits being added
					for(const v of mutable.visits) {
						if(!v.dateCreated && !mutableVisits.some(mv => mv.key === v.key)) {
							mutableVisits.push(v);
						}
					}
				}

				mutable = operationsModelFacadeFactory({ job, jobCosts: contracts.toArray(), visits: mutableVisits});
			}

			return [ mutable, rebase ];
		},
		save: async (doc: { job: IJob, jobCosts: IJobCost[], visits: ISiteVisit[] }, patch: IPatchOperation[]) => {
			// DR - saves need to be run serially to avoid api duplicate request pruning
			for(const p of patch) {

				if(p.path.indexOf('job/') === 0) {
					await jobActions.saveJob({ job: doc.job, patch: p.value });
				} else if(p.path.indexOf('jobCost/') === 0) {
					const jobCost = doc.jobCosts.find(jc => jc.key === p.path.replace('jobCost/', ''));

					await jobCostActions.saveJobCost({ jobCost, patch: p.value });
				} else if(p.path.indexOf('visit') === 0) {
					if(p.op === PatchOpType.add) {
						await siteVisitActions.saveSiteVisit({ siteVisit: p.value });
					}
					else if(p.op === PatchOpType.replace) {
						const visit = doc.visits.find(v => v.key === p.path.replace('visit/', ''));
	
						if(visit) {
							await siteVisitActions.saveSiteVisit({ siteVisit: visit, patch: p.value });
						}
					}
					else if(p.op === PatchOpType.remove) {
						await siteVisitActions.deleteSiteVisit({ siteVisit: { key: p.path.replace('visit/', '') } });
					}
				}
			}
		},
		processAlert(alert) {			
			return alert;
		},
		validate(model){
			const messages = [];
			for(const v of model.visits) {
				v.test(undefined, messages);
			}
			return messages;
		}
	});
	const vistables = saveRoot.model? componentServices.operationsHelper.getVisitables(saveRoot.model.job.key): [];
	const installConfigs = useMemo(() => {
		const newConfigs = (saveRoot.model?.jobCosts || []).reduce((map, c) => {
			const { installConfigName, installationConfig } = actions.getInstallationConfig(c);
			const items = installationConfig?.items || installationConfig;
			let groups = map[installConfigName]?.groups;

			if(items && !groups) {
				groups = items.reduce((g, i) => {
					const groupName = i.opsGroupName || i.group;
					const { description, type } = i;

					let group = g.find(v => v.name === groupName);
					if(!group) {
						group = { name: groupName, tests: [] };
						g.push(group);
					}
					group.tests.push((li) => li.type === type && li.group === groupName && li.description === description);

					return g;
				}, []);
			}

			return {...map, ...{ [installConfigName]: { groups } }};
		}, {});

		return newConfigs;

	}, [saveRoot.model?.jobCosts, store.getIn(['model', 'config'])])
	const addVisit = useCallback((type: SiteVisitType, selections: IContractSelection[], callback?: () => void) => {
		if(!selections.filter(s => s.selected).length) {
			return;
		}

		saveRoot.update(facade => {
			facade.addVisit(type, selections);

			if(callback) {
				callback();
			}
		}, { autoSave: true });
	}, [saveRoot.model]);

	const updateSelectedContracts = useCallback((siteVisitKey: string, selections: IContractSelection[], callback?: () => void) => {
		if(!selections.filter(s => s.selected).length) {
			return;
		}
		saveRoot.update(facade => {
			facade.updateVisitSelections(siteVisitKey, selections);
			if(callback) {
				callback();
			}
		}, { autoSave: true });
	}, [saveRoot.model]);

	const updateNotes = useCallback((updater: (notes: IModelSet<IJobNote>)=>void, options: { autoSave: boolean }) => {
		saveRoot.update(facade => {
			updater(facade.job.notes);
		}, options);
	}, [saveRoot.model])

	const updateContracts = useCallback((updater: (contracts: IJobCost[])=>void, options: { autoSave: boolean }) => {
		saveRoot.update(facade => {
			updater(facade.jobCosts);
		}, options);
	}, [saveRoot.model])

	const deleteVisit = useCallback((siteVisitKey: string, callback?: () => void) => {
		saveRoot.update(facade => {
			facade.removeVisit(siteVisitKey);
			if(callback) {
				callback();
			}
		}, {autoSave: true});
	}, [saveRoot.model]);

	const setVisitStatus = useCallback((siteVisitKey: string, status: SiteVisitStatus, effectiveDate: Date, description: string) => {
		saveRoot.update(facade => {
			const visit = facade.visits.find(v => v.key === siteVisitKey);

			visit.statusLog.add({ status, description, effectiveDate });
		}, { autoSave: true });
	}, [saveRoot.model]);

	const setScheduledDate = useCallback((siteVisitKey: string, date: Date) => {
		setVisitStatus(siteVisitKey, SiteVisitStatus.scheduled, date, undefined);
	}, [setVisitStatus]);

	const setPrimaryEmployee = useCallback((siteVisitKey: string, employeeKey: string) => {
		saveRoot.update(facade => {
			const visit = facade.visits.find(v => v.key === siteVisitKey);

			if(visit) {
				visit.setPrimaryEmployee(employeeKey)
			}

		}, { autoSave: true });
	}, [saveRoot.model]);

	const setVisitStringAt = useCallback((e: { type: string, target: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement }, siteVisitKey: string, path: string) => {
		const autoSave = e.type === 'blur' || e.target.nodeName === 'SELECT';
		saveRoot.update(facade => {
			const visit = facade.visits.find(v => v.key === siteVisitKey);
			const val = autoSave? valueFilters.clean(e.target.value): e.target.value; 

			if(visit) {
				visit.setAt(path, val);
			}

		}, { autoSave });
	}, [saveRoot.model]);

	const setRequiresDelivery = useCallback((item: IOperationsItem, val: boolean) => {
		saveRoot.update(facade => {
			const jobCost = facade.jobCosts.find(jc => jc.key === item.jobCostKey);
			const target = item.changeOrderKey? jobCost.changeOrders.get(item.changeOrderKey): jobCost;
			
			target.requiresDelivery = val;

		}, { autoSave: true });
	}, [saveRoot.model]);

	const setRequiresInstall = useCallback((item: IOperationsItem, val: boolean) => {
		saveRoot.update(facade => {
			const jobCost = facade.jobCosts.find(jc => jc.key === item.jobCostKey);
			const target = item.changeOrderKey? jobCost.changeOrders.get(item.changeOrderKey): jobCost;
			
			target.requiresInstall = val;

		}, { autoSave: true });
	}, [saveRoot.model]);

	const { model } = saveRoot;

	useEffect(() => {
		contractorActions.fetchContractors({
			filters: { type: enums.contractorTypes.Delivery_Company }
		})
	}, []);

	const deliveryCompanies = getCollection({ store, collection: ModelCollection.contractors}).filter(c => (c.type & enums.contractorTypes.Delivery_Company) === enums.contractorTypes.Delivery_Company).toArray();
	return model? <div>
		<OverviewSection items={ vistables } setRequiresDelivery={setRequiresDelivery} setRequiresInstall={setRequiresInstall} />
		<DeliveryBucket contracts={ model.jobCosts } visits={ model.deliveries } deliveryCompanies={ deliveryCompanies } addVisit={ addVisit } updateSelectedContracts={ updateSelectedContracts } deleteVisit={ deleteVisit } setPrimaryEmployee={setPrimaryEmployee} setScheduledDate={ setScheduledDate } setVisitStringAt={ setVisitStringAt }/>
		<IntstallBucket contracts={ model.jobCosts } installConfigs={ installConfigs } visits={ model.installations } addVisit={ addVisit } setVisitStatus={ setVisitStatus } updateContracts={ updateContracts } updateSelectedContracts={ updateSelectedContracts } deleteVisit={ deleteVisit } setPrimaryEmployee={setPrimaryEmployee} setScheduledDate={ setScheduledDate } setVisitStringAt={ setVisitStringAt } />
		<JobNotes key={ job.key } notes={ job.notes.filter(n => n.type === 'ops-internal') } title="Internal Ops Notes" tooltipText="Will not be displayed on any client documents" type={ 'ops-internal' } updateNotes={updateNotes} />
		<HistorySections contracts={model.jobCosts} emptyTitle="No History" />
	</div>: null;
}